import type { ReactNode } from 'react'
import { Check, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { OfficeAppIcon } from '@/components/landing/OfficeAppIcon'
import { DocThumb } from './DocThumb'
import { BRAND, halftone } from './halftone'

/* ----------------------------------------------------------------------------
   Portrait product-UI tiles for the platform hero's dock marquee: template
   thumbnails, a slide, a chart, a task pane, brand swatches, a prompt box, an
   audit list. Static JSX, sized by their tile (fills its parent).
---------------------------------------------------------------------------- */

const frame = 'h-full w-full overflow-hidden'
const line = 'block h-[3px] bg-[var(--color-landing-gray-200)] dark:bg-[var(--color-landing-gray-700)]'
const white = 'border border-[var(--color-landing-gray-200)] bg-white dark:border-[var(--color-landing-gray-700)] dark:bg-[#16161f]'

function ChartTile() {
  return (
    <div className={cn(frame, white, 'flex flex-col gap-3 p-4')}>
      <span className="text-[0.6rem] font-bold uppercase tracking-wide text-[var(--color-landing-gray-900)] dark:text-white">Report</span>
      <span className="flex flex-1 items-end gap-1.5 border-b border-l border-[var(--color-landing-gray-200)] px-1.5 dark:border-[var(--color-landing-gray-700)]">
        {[40, 62, 48, 80, 70, 94].map((h, i) => (
          <span key={i} className="block flex-1" style={{ height: `${h}%`, backgroundColor: i === 5 ? BRAND.pink : BRAND.blue }} />
        ))}
      </span>
      <span className={cn(line, 'w-full')} />
      <span className={cn(line, 'w-2/3')} />
    </div>
  )
}

function TaskPaneTile() {
  return (
    <div className={cn(frame, white, 'flex flex-col gap-2.5 p-3')}>
      <span className="flex items-center gap-1.5">
        <OfficeAppIcon app="word" className="h-5 w-5" />
        <span className="text-[0.6rem] font-bold uppercase tracking-wide text-[var(--color-landing-gray-900)] dark:text-white">Templates</span>
      </span>
      {[BRAND.blue, BRAND.purple, BRAND.pink, BRAND.blue].map((c, i) => (
        <span
          key={i}
          className={cn(
            'flex items-center gap-2 border p-1.5',
            i === 1 ? 'border-[var(--color-landing-accent)]' : 'border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-700)]'
          )}
        >
          <span className="block h-6 w-5 shrink-0" style={{ backgroundColor: c, ...halftone(0.3, 4) }} />
          <span className="flex flex-1 flex-col gap-1">
            <span className={cn(line, 'w-full')} />
            <span className={cn(line, 'w-1/2')} />
          </span>
        </span>
      ))}
      <span className="mt-auto bg-[var(--color-landing-primary)] py-1.5 text-center text-[0.55rem] font-bold uppercase text-white dark:bg-white dark:text-[var(--color-landing-primary)]">
        Insert
      </span>
    </div>
  )
}

function SwatchTile() {
  return (
    <div className={cn(frame, 'flex flex-col')}>
      {[
        [BRAND.blue, '#0C00FF'],
        [BRAND.purple, '#B200FF'],
        [BRAND.pink, '#FF00F2'],
        ['#0a0a0a', '#0A0A0A'],
      ].map(([c, hex]) => (
        <span key={hex} className="flex flex-1 items-end p-2.5" style={{ backgroundColor: c }}>
          <span className="font-[family-name:var(--font-landing-mono)] text-[0.6rem] text-white">{hex}</span>
        </span>
      ))}
    </div>
  )
}

function PromptTile() {
  return (
    <div className={cn(frame, 'flex flex-col gap-3 bg-[var(--color-landing-primary)] p-4 text-white')}>
      <span className="flex items-center gap-1.5 text-[0.6rem] font-bold uppercase tracking-wide">
        <Sparkles className="h-3.5 w-3.5" style={{ color: BRAND.pink }} /> Templatean AI
      </span>
      <span className="border border-white/25 p-2 text-[0.65rem] leading-snug">Draft a proposal from our winning template</span>
      <span className="flex flex-col gap-1.5">
        {[100, 86, 94, 60].map((w, i) => (
          <span key={i} className="block h-[3px] bg-white/35" style={{ width: `${w}%` }} />
        ))}
      </span>
      <span className="mt-auto inline-block h-3 w-1.5 bg-[#ff00f2]" />
    </div>
  )
}

function AuditTile() {
  return (
    <div className={cn(frame, white, 'flex flex-col gap-2 p-3')}>
      <span className="text-[0.6rem] font-bold uppercase tracking-wide text-[var(--color-landing-gray-900)] dark:text-white">Audit log</span>
      {Array.from({ length: 6 }).map((_, i) => (
        <span key={i} className="flex items-center gap-2 border-b border-[var(--color-landing-gray-200)] pb-1.5 dark:border-[var(--color-landing-gray-700)]">
          <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center text-white" style={{ backgroundColor: BRAND.green }}>
            <Check className="h-2.5 w-2.5" strokeWidth={3} />
          </span>
          <span className={cn(line, 'flex-1')} />
        </span>
      ))}
    </div>
  )
}

function FormulaTile() {
  return (
    <div className={cn(frame, 'relative flex flex-col justify-between p-4 text-white')} style={{ backgroundColor: BRAND.blue }}>
      <span className="absolute inset-0" style={halftone(0.22, 5)} />
      <OfficeAppIcon app="excel" className="relative h-9 w-9" />
      <span className="relative font-[family-name:var(--font-landing-mono)] text-[0.7rem] leading-snug">
        <span className="italic opacity-70">fx </span>=SUMIFS(
        <br />
        Sales[Amount],
        <br />
        Sales[Region], A2)
      </span>
    </div>
  )
}

function AssetGridTile() {
  return (
    <div className={cn(frame, white, 'grid grid-cols-2 gap-1.5 p-2.5')}>
      {[BRAND.blue, 'logo', BRAND.pink, 'font', BRAND.purple, '#0a0a0a'].map((c, i) =>
        c === 'logo' ? (
          <span key={i} className="flex items-center justify-center border border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-700)]">
            <span className="flex h-7 w-7 items-center justify-center font-[family-name:var(--font-landing-display)] text-xs text-white" style={{ backgroundColor: BRAND.blue }}>T</span>
          </span>
        ) : c === 'font' ? (
          <span key={i} className="flex items-center justify-center border border-[var(--color-landing-gray-200)] font-[family-name:var(--font-landing-display)] text-base text-[var(--color-landing-gray-900)] dark:border-[var(--color-landing-gray-700)] dark:text-white">Aa</span>
        ) : (
          <span key={i} className="block" style={{ backgroundColor: c, ...halftone(0.3, 5) }} />
        )
      )}
    </div>
  )
}

function AppTile({ app, color }: { app: 'word' | 'powerpoint' | 'outlook'; color: string }) {
  return (
    <div className={cn(frame, 'relative flex items-center justify-center')} style={{ backgroundColor: color }}>
      <span className="absolute inset-0" style={halftone(0.3, 6)} />
      <span className="relative flex h-16 w-16 items-center justify-center bg-white">
        <OfficeAppIcon app={app} className="h-12 w-12" />
      </span>
    </div>
  )
}

const thumb = 'h-full w-full'

/** The tile set, keyed so a page can reorder it. */
export const PRODUCT_TILES: Array<{ key: string; node: ReactNode }> = [
  { key: 'proposal', node: <DocThumb kind="doc" title="Proposal" className={thumb} /> },
  { key: 'slide', node: <DocThumb kind="slide" title="Board deck" accent="purple" className={thumb} /> },
  { key: 'chart', node: <ChartTile /> },
  { key: 'word', node: <AppTile app="word" color={BRAND.pink} /> },
  { key: 'taskpane', node: <TaskPaneTile /> },
  { key: 'swatches', node: <SwatchTile /> },
  { key: 'prompt', node: <PromptTile /> },
  { key: 'sheet', node: <DocThumb kind="sheet" title="Budget" accent="green" className={thumb} /> },
  { key: 'formula', node: <FormulaTile /> },
  { key: 'mail', node: <DocThumb kind="mail" title="Follow-up" accent="pink" className={thumb} /> },
  { key: 'assets', node: <AssetGridTile /> },
  { key: 'powerpoint', node: <AppTile app="powerpoint" color={BRAND.purple} /> },
  { key: 'audit', node: <AuditTile /> },
  { key: 'contract', node: <DocThumb kind="doc" title="Contract" accent="purple" className={thumb} /> },
  { key: 'outlook', node: <AppTile app="outlook" color={BRAND.blue} /> },
]

/** The tile set rotated to start at `first`, so each platform page leads with its own tile. */
export function productTiles(first?: string): ReactNode[] {
  const start = Math.max(0, PRODUCT_TILES.findIndex((t) => t.key === first))
  return [...PRODUCT_TILES.slice(start), ...PRODUCT_TILES.slice(0, start)].map((t) => t.node)
}
